import { useEffect, useState, Fragment } from "react";
import { api, formatApiError, ksh, API } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Loader2, Download, ChevronDown, ChevronUp,
  UserCircle2, UserPlus, History,
} from "lucide-react";
import KanbanBoard from "@/components/admin/KanbanBoard";
import { devLog } from "@/lib/devLog";
import {
  STATUS_OPTIONS, STATUS_LABEL, WORKFLOW_STATUSES, PAYMENT_STATUSES,
  hasPerm, maskPhone, timeAgo, fmtNairobi,
} from "@/components/admin/_shared";

export default function OrdersTab({ user }) {
  const isSuper = user?.role === "super_admin" || user?.role === "admin";
  const canPay = hasPerm(user, "orders.update_payment");
  const canStatus = hasPerm(user, "orders.update_status");
  const canDispatch = hasPerm(user, "orders.dispatch");
  const canClaim = hasPerm(user, "orders.claim");
  const canReassign = isSuper;
  const canAudit = isSuper || hasPerm(user, "audit.view");
  const canPII = hasPerm(user, "orders.view_pii");

  const [orders, setOrders] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [auditMap, setAuditMap] = useState({}); // order_id → audit list

  // --- Filters ---
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [timeFilter, setTimeFilter] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [sortBy, setSortBy] = useState("latest");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("table"); // table | board

  const load = async () => {
    setLoading(true);
    const reqs = [api.get("/admin/orders")];
    if (isSuper) reqs.push(api.get("/admin/staff"));
    const [ord, st] = await Promise.all(reqs);
    setOrders(ord.data);
    if (st) setStaff(st.data.filter((u) => u.role === "staff" && u.active !== false));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Status → ${STATUS_LABEL[status]}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  const claimOrder = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/assign`, { staff_id: user.id });
      toast.success("Order claimed");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Claim failed");
    }
  };

  const releaseOrder = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/assign`, { staff_id: null });
      toast.success("Order released");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Release failed");
    }
  };

  const reassignOrder = async (id, staffId) => {
    try {
      await api.post(`/admin/orders/${id}/assign`, { staff_id: staffId || null });
      toast.success(staffId ? "Order reassigned" : "Order unassigned");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Reassign failed");
    }
  };

  const loadAudit = async (orderId) => {
    if (!canAudit) return;
    try {
      const { data } = await api.get(`/admin/orders/${orderId}/audit`);
      setAuditMap((m) => ({ ...m, [orderId]: data }));
    } catch (err) { devLog.debug("audit fetch failed", err); }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const token = localStorage.getItem("ctp_token");
      const url = `${API}/admin/files/${fileId}/download`;
      const resp = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        // Surface the actual reason (e.g. "Order is paid but not yet claimed
        // for printing. Mark it `printing` to download.") instead of a
        // generic toast — staff can't fix what they can't see.
        let detail = `HTTP ${resp.status}`;
        try {
          const body = await resp.json();
          if (body?.detail) detail = formatApiError(body.detail);
        } catch { /* body wasn't JSON — keep the HTTP code */ }
        toast.error(detail);
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      // Release the blob URL once the click has been dispatched.
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (err) {
      devLog.debug("download failed", err);
      toast.error("Could not download file — check your connection and try again.");
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#059669]" />;

  // ---- Filter + sort ----
  const inTimeWindow = (iso) => {
    if (timeFilter === "all") return true;
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    if (timeFilter === "today") return d.toDateString() === now.toDateString();
    if (timeFilter === "week") {
      const wk = new Date(now); wk.setDate(now.getDate() - 7);
      return d >= wk;
    }
    if (timeFilter === "month") {
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }
    return true;
  };
  const matchesSearch = (o) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (o.tracking_number || "").toLowerCase().includes(q) ||
      (o.customer_name || "").toLowerCase().includes(q) ||
      (o.customer_phone || "").toLowerCase().includes(q)
    );
  };
  const visibleOrders = orders
    .filter((o) => statusFilter.size === 0 || statusFilter.has(o.status))
    .filter((o) => inTimeWindow(o.created_at))
    .filter((o) => {
      if (assignedFilter === "all") return true;
      if (assignedFilter === "mine") return o.assigned_to === user.id;
      if (assignedFilter === "unassigned") return !o.assigned_to;
      return o.assigned_to === assignedFilter;
    })
    .filter((o) => {
      if (paymentFilter === "all") return true;
      if (paymentFilter === "paid") return o.payment_status === "paid";
      return o.payment_status !== "paid";
    })
    .filter(matchesSearch)
    .sort((a, b) => {
      if (sortBy === "latest") return (b.created_at || "").localeCompare(a.created_at || "");
      if (sortBy === "oldest") return (a.created_at || "").localeCompare(b.created_at || "");
      if (sortBy === "total_desc") return (b.total || 0) - (a.total || 0);
      if (sortBy === "total_asc") return (a.total || 0) - (b.total || 0);
      return 0;
    });

  const toggleStatus = (s) => {
    const next = new Set(statusFilter);
    if (next.has(s)) next.delete(s); else next.add(s);
    setStatusFilter(next);
  };
  const resetFilters = () => {
    setStatusFilter(new Set());
    setTimeFilter("all");
    setAssignedFilter("all");
    setPaymentFilter("all");
    setSortBy("latest");
    setSearch("");
  };
  const filtersActive =
    statusFilter.size > 0 || timeFilter !== "all" || assignedFilter !== "all" ||
    paymentFilter !== "all" || sortBy !== "latest" || search.trim().length > 0;

  const exportCSV = () => {
    const headers = [
      "Tracking", "Created", "Updated", "Customer", "Phone", "Email",
      "Items", "Subtotal", "Total", "Payment", "Status", "Assigned",
      "Delivery", "Address", "Notes",
    ];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = visibleOrders.map((o) => [
      o.tracking_number,
      o.created_at,
      o.updated_at,
      o.customer_name,
      o.customer_phone,
      o.customer_email || "",
      o.items?.length || 0,
      o.subtotal ?? "",
      o.total ?? "",
      o.payment_status || "",
      o.status || "",
      o.assigned_to_name || "",
      o.delivery_method || "",
      o.delivery_address || "",
      (o.notes || "").replace(/\n/g, " "),
    ].map(esc).join(","));
    const csv = "\uFEFF" + [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, "");
    a.href = url;
    a.download = `ctp-orders-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${visibleOrders.length} orders`);
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const Chip = ({ active, onClick, children, testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold border transition-colors whitespace-nowrap ${active ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"}`}
    >
      {children}
    </button>
  );

  // Build status options based on permissions + payment guard
  const statusOptionsFor = (o) => {
    const paid = o.payment_status === "paid";
    return STATUS_OPTIONS.filter((s) => {
      if (PAYMENT_STATUSES.has(s)) return canPay;
      if (s === "completed") return canDispatch && paid;
      if (WORKFLOW_STATUSES.has(s)) return canStatus && paid;
      return false;
    });
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bg-white border border-[#E5E5E5] p-4 space-y-3" data-testid="orders-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tracking #, name or phone..."
            data-testid="orders-search"
            className="flex-1 min-w-[200px] px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm"
          />
          <div className="flex gap-1" data-testid="view-toggle">
            <button
              onClick={() => setView("table")}
              data-testid="view-table"
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border ${view === "table" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
            >Table</button>
            <button
              onClick={() => setView("board")}
              data-testid="view-board"
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border ${view === "board" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
            >Board</button>
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="orders-sort"
            className="px-2 py-2 border border-[#0A0A0A] text-[10px] uppercase tracking-[0.15em] font-bold bg-white"
          >
            <option value="latest">Sort: Latest</option>
            <option value="oldest">Sort: Oldest</option>
            <option value="total_desc">Sort: Highest total</option>
            <option value="total_asc">Sort: Lowest total</option>
          </select>
          <button
            onClick={exportCSV}
            disabled={visibleOrders.length === 0}
            data-testid="orders-export-csv"
            style={{ display: canPII ? "inline-flex" : "none" }}
            className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border border-[#059669] text-[#059669] hover:bg-[#059669] hover:text-white disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-[#059669]"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          {filtersActive && (
            <button
              onClick={resetFilters}
              data-testid="orders-reset-filters"
              className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
            >
              Reset
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mr-1">Status:</span>
          {STATUS_OPTIONS.map((s) => (
            <Chip key={s} active={statusFilter.has(s)} onClick={() => toggleStatus(s)} testId={`filter-status-${s}`}>
              {STATUS_LABEL[s]}
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mr-1">Time:</span>
            {[
              { v: "today", l: "Today" },
              { v: "week", l: "This week" },
              { v: "month", l: "This month" },
              { v: "all", l: "All time" },
            ].map((t) => (
              <Chip key={t.v} active={timeFilter === t.v} onClick={() => setTimeFilter(t.v)} testId={`filter-time-${t.v}`}>
                {t.l}
              </Chip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mr-1">Payment:</span>
            {[
              { v: "all", l: "All" },
              { v: "paid", l: "Paid" },
              { v: "unpaid", l: "Unpaid" },
            ].map((t) => (
              <Chip key={t.v} active={paymentFilter === t.v} onClick={() => setPaymentFilter(t.v)} testId={`filter-payment-${t.v}`}>
                {t.l}
              </Chip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mr-1">Assigned:</span>
            <Chip active={assignedFilter === "all"} onClick={() => setAssignedFilter("all")} testId="filter-assigned-all">All</Chip>
            <Chip active={assignedFilter === "mine"} onClick={() => setAssignedFilter("mine")} testId="filter-assigned-mine">Mine</Chip>
            <Chip active={assignedFilter === "unassigned"} onClick={() => setAssignedFilter("unassigned")} testId="filter-assigned-unassigned">Unassigned</Chip>
            {isSuper && staff.length > 0 && (
              <select
                value={["all", "mine", "unassigned"].includes(assignedFilter) ? "" : assignedFilter}
                onChange={(e) => e.target.value && setAssignedFilter(e.target.value)}
                data-testid="filter-assigned-staff"
                className="px-2 py-1.5 border border-[#E5E5E5] text-[10px] uppercase tracking-[0.15em] font-bold bg-white"
              >
                <option value="">— pick staff —</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] pt-1 border-t border-[#E5E5E5]" data-testid="orders-counter">
          Showing <span className="text-[#0A0A0A]">{visibleOrders.length}</span> of {orders.length} orders
        </div>
      </div>

      {view === "table" && (
      <div className="border border-[#E5E5E5] bg-white overflow-x-auto" data-testid={ADMIN.ordersTable}>
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Tracking</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Created</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Customer</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Items</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Total</th>
              {canPay && <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Payment</th>}
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Status</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Assigned</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Updated</th>
              <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]"></th>
            </tr>
          </thead>
          <tbody>
            {visibleOrders.length === 0 && (
              <tr><td colSpan={canPay ? 10 : 9} className="p-8 text-center text-[#525252]">No orders.</td></tr>
            )}
            {visibleOrders.map((o) => {
              const isMine = o.assigned_to === user.id;
              const locked = o.assigned_to && !isMine && !isSuper;
              return (
                <Fragment key={o.id}>
                  <tr className={`border-b border-[#E5E5E5] last:border-b-0 ${locked ? "opacity-50" : "hover:bg-[#F5F5F5]"}`}>
                    <td className="p-3 mono font-bold">{o.tracking_number}</td>
                    <td
                      className="p-3 text-xs text-[#525252] mono whitespace-nowrap"
                      title={fmtNairobi(o.created_at)}
                      data-testid={`order-created-${o.tracking_number}`}
                    >
                      {timeAgo(o.created_at)}
                    </td>
                    <td className="p-3">
                      <div className="font-bold">{o.customer_name}</div>
                      <div className="text-xs text-[#525252] mono" data-testid={`order-phone-${o.tracking_number}`}>
                        {canPII ? o.customer_phone : maskPhone(o.customer_phone)}
                      </div>
                    </td>
                    <td className="p-3 text-[#525252]">{o.items.length}</td>
                    <td className="p-3 mono font-bold">
                      <div>{ksh(o.total)}</div>
                      {o.tier && o.tier !== "normal" && (
                        <div
                          data-testid={`tier-badge-${o.tracking_number}`}
                          className={`mt-1 inline-block px-2 py-0.5 border text-[9px] uppercase tracking-[0.15em] font-bold ${
                            o.tier === "priority" ? "border-[#DC2626] bg-[#DC2626]/10 text-[#DC2626]" : "border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E]"
                          }`}
                        >
                          {o.tier === "priority" ? "⚡ Priority" : "⚡ Urgent"}
                        </div>
                      )}
                      {o.payment_status !== "paid" && (
                        <div
                          data-testid={`payment-pending-${o.tracking_number}`}
                          className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 border border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E] text-[9px] uppercase tracking-[0.15em] font-bold"
                          title="Workflow status changes are blocked until this order is marked paid."
                        >
                          ⚠ Payment Pending
                        </div>
                      )}
                    </td>
                    {canPay && (
                      <td className="p-3">
                        <span className={`inline-block px-2 py-1 border text-[10px] uppercase tracking-[0.15em] font-bold ${o.payment_status === "paid" ? "border-[#059669] text-[#059669]" : "border-[#F59E0B] text-[#F59E0B]"}`}>
                          {o.payment_status}
                        </span>
                      </td>
                    )}
                    <td className="p-3">
                      {(canStatus || canPay) && !locked ? (
                        <select
                          data-testid={`${ADMIN.statusSelect}-${o.tracking_number}`}
                          value={o.status}
                          onChange={(e) => updateStatus(o.id, e.target.value)}
                          className="px-2 py-1 border border-[#0A0A0A] text-xs font-bold uppercase tracking-[0.1em] bg-white"
                        >
                          {statusOptionsFor(o).map((s) => (
                            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                          ))}
                          {!statusOptionsFor(o).includes(o.status) && (
                            <option key={o.status} value={o.status} disabled>
                              {STATUS_LABEL[o.status]}
                            </option>
                          )}
                        </select>
                      ) : (
                        <span className="inline-block px-2 py-1 border text-[10px] uppercase tracking-[0.15em] font-bold border-[#525252] text-[#525252]">
                          {STATUS_LABEL[o.status]}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs whitespace-nowrap" data-testid={`order-assigned-${o.tracking_number}`}>
                      {o.assigned_to_name ? (
                        <div className="flex items-center gap-2">
                          <UserCircle2 className="w-4 h-4 text-[#059669]" />
                          <span className="font-bold">{o.assigned_to_name}</span>
                          {isMine && !isSuper && (
                            <button onClick={() => releaseOrder(o.id)} data-testid={`release-${o.tracking_number}`} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#DC2626] hover:underline">
                              release
                            </button>
                          )}
                          {canReassign && (
                            <select
                              onChange={(e) => reassignOrder(o.id, e.target.value)}
                              value=""
                              data-testid={`reassign-${o.tracking_number}`}
                              className="text-[10px] uppercase tracking-[0.15em] font-bold border border-[#E5E5E5] px-1 py-0.5 bg-white"
                            >
                              <option value="">⋯ reassign</option>
                              {staff.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                              <option value="">— Unassign —</option>
                            </select>
                          )}
                        </div>
                      ) : canClaim && !isSuper ? (
                        <button
                          onClick={() => claimOrder(o.id)}
                          data-testid={`claim-${o.tracking_number}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#059669] text-white text-[10px] uppercase tracking-[0.15em] font-bold hover:bg-[#047857]"
                        >
                          <UserPlus className="w-3 h-3" /> Claim
                        </button>
                      ) : canReassign ? (
                        <select
                          onChange={(e) => e.target.value && reassignOrder(o.id, e.target.value)}
                          value=""
                          data-testid={`assign-${o.tracking_number}`}
                          className="text-[10px] uppercase tracking-[0.15em] font-bold border border-[#E5E5E5] px-1 py-0.5 bg-white"
                        >
                          <option value="">⋯ assign</option>
                          {staff.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-[#525252]">—</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-[#525252] mono whitespace-nowrap" data-testid={`order-updated-${o.tracking_number}`}>
                      {o.updated_at ? new Date(o.updated_at).toLocaleString("en-KE", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => { setExpanded(expanded === o.id ? null : o.id); if (expanded !== o.id) loadAudit(o.id); }}
                        className="w-8 h-8 border border-[#E5E5E5] hover:border-[#0A0A0A] flex items-center justify-center"
                      >
                        {expanded === o.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </td>
                  </tr>
                  {expanded === o.id && (
                    <tr key={o.id + "-exp"} className="bg-[#F5F5F5]">
                      <td colSpan={canPay ? 10 : 9} className="p-4">
                        <div className="flex flex-wrap gap-x-6 gap-y-1 mb-3 text-[11px] mono text-[#525252]">
                          <span data-testid={`order-created-abs-${o.tracking_number}`}>
                            <b className="text-[#0A0A0A]">Created:</b> {fmtNairobi(o.created_at)}
                          </span>
                          {o.updated_at && o.updated_at !== o.created_at && (
                            <span>
                              <b className="text-[#0A0A0A]">Updated:</b> {fmtNairobi(o.updated_at)}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-3">§ Files & Specifications</div>
                        <ul className="space-y-2">
                          {o.items.map((it, i) => {
                            const bindDesc = describeBinding(it);
                            const engraving = it.binding_gold_engraving ? (
                              it.binding_engraving_source === "upload"
                                ? `gold engraving · uploaded: ${it.binding_engraving_file_name || "—"}`
                                : "gold engraving · uses cover-page details"
                            ) : null;
                            return (
                            <li key={`${o.id}-${i}`} className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#E5E5E5] p-3">
                              <div className="min-w-0">
                                <div className="font-bold truncate">{it.file_name}</div>
                                <div className="text-xs text-[#525252] mono">
                                  {it.copies}× · {it.paper_size} · {colorModeLabel(it)} · {printPagesLabel(it)}
                                  {it.double_sided && " · 2-sided"}
                                  {it.stapling && " · stapled"}
                                  {bindDesc && ` · ${bindDesc}`}
                                  {it.lamination && " · laminated"}
                                  {it.batch_copies && ` · BATCH (${(it.pages || 1) * (it.copies || 1)}pg combined)`}
                                  {it.notes && ` · Note: ${it.notes}`}
                                </div>
                                {engraving && (
                                  <div className="text-[11px] text-[#059669] mono font-bold mt-1" data-testid={`engraving-info-${it.file_id}`}>
                                    ★ {engraving}
                                    {it.binding_engraving_source === "upload" && it.binding_engraving_file_id && (
                                      <button
                                        onClick={() => downloadFile(it.binding_engraving_file_id, it.binding_engraving_file_name || "engraving")}
                                        className="ml-2 underline hover:text-[#0A0A0A]"
                                      >
                                        Download artwork
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="mono font-bold">{ksh(it.cost)}</span>
                                <button
                                  data-testid={`${ADMIN.downloadFile}-${it.file_id}`}
                                  onClick={() => downloadFile(it.file_id, it.file_name)}
                                  className="inline-flex items-center gap-1 px-3 py-2 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.15em] font-bold hover:bg-[#059669]"
                                >
                                  <Download className="w-3.5 h-3.5" /> Download
                                </button>
                              </div>
                            </li>
                          );})}
                        </ul>
                        {o.notes && (
                          <div className="mt-3 text-sm text-[#525252]">
                            <strong className="text-[#0A0A0A]">Notes:</strong> {o.notes}
                          </div>
                        )}
                        {o.delivery_method === "delivery" && o.delivery_address && (
                          <div className="mt-2 text-sm text-[#525252]">
                            <strong className="text-[#0A0A0A]">Delivery:</strong> {o.delivery_address}
                          </div>
                        )}

                        {canAudit && (
                          <div className="mt-4 border-t border-[#E5E5E5] pt-3">
                            <div className="flex items-center gap-2 mb-2">
                              <History className="w-4 h-4 text-[#525252]" />
                              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">§ Audit trail</div>
                            </div>
                            <ul className="space-y-1.5" data-testid={`audit-${o.tracking_number}`}>
                              {(auditMap[o.id] || []).length === 0 && (
                                <li className="text-xs text-[#525252] italic">No actions logged yet.</li>
                              )}
                              {(auditMap[o.id] || []).map((a) => (
                                <li key={a.id} className="flex flex-wrap items-baseline gap-2 text-xs">
                                  <span className="mono text-[#525252] whitespace-nowrap">{new Date(a.created_at).toLocaleString("en-KE", { dateStyle: "short", timeStyle: "short" })}</span>
                                  <span className="font-bold">{a.actor_name}</span>
                                  <span className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border border-[#E5E5E5] text-[#525252]">{a.actor_role}</span>
                                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#059669]">{a.action}</span>
                                  {a.before && <span className="text-[#525252]"><s>{a.before}</s></span>}
                                  {a.after && <span className="text-[#0A0A0A] font-bold">→ {a.after}</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {view === "board" && (
        <KanbanBoard
          orders={visibleOrders}
          user={user}
          canStatus={canStatus}
          canDispatch={canDispatch}
          updateStatus={updateStatus}
        />
      )}
    </div>
  );
}

function describeBinding(it) {
  if (!it.binding || it.binding === "none") return "";
  const top = it.binding_top_cover || it.binding_cover || "blue";
  if (it.binding === "hardcover") {
    return `hardcover · ${top} both sides`;
  }
  const back = it.binding_back_cover || "blue";
  return `${it.binding}-bound · top: ${top} · back: ${back}`;
}

function colorModeLabel(it) {
  const mode = it.color_mode || "bw";
  if (mode === "color") return "All Color";
  if (mode === "bw") return "B&W";
  const pages = it.color_pages || [];
  if (!pages.length) {
    return mode === "auto" ? "Auto → all B&W" : "Custom → all B&W";
  }
  const ranges = compactRanges(pages);
  return mode === "auto"
    ? `Auto · color pages: ${ranges}`
    : `Custom · color pages: ${ranges}`;
}

function printPagesLabel(it) {
  if (it.print_all_pages !== false) return `${it.pages}pg`;
  const list = it.print_pages || [];
  const ranges = compactRanges(list);
  return `${list.length}/${it.pages}pg${ranges ? ` (${ranges})` : ""}`;
}

function compactRanges(pages) {
  if (!pages?.length) return "";
  const sorted = [...pages].sort((a, b) => a - b);
  const groups = [];
  let start = sorted[0], prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === prev + 1) { prev = sorted[i]; continue; }
    groups.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = sorted[i]; prev = sorted[i];
  }
  groups.push(start === prev ? `${start}` : `${start}-${prev}`);
  return groups.join(", ");
}

